/**
 * A shell-owned pub/sub bridge for cross-app event-composer coordination
 * (sider arbitration, draft patches, lifecycle events).
 *
 * WHY THIS EXISTS INSTEAD OF `window` EVENTS: each micro-frontend bundles its
 * own copy of `@robinpowered/dashboard-apps-common`, so a module-level
 * singleton here would be a *different* instance per MFE bundle and couldn't
 * carry messages between apps. The shell therefore creates ONE bridge instance
 * (see `createShellComposerBridge`) and hands the same object reference to every
 * MFE via single-spa `customProps` — exactly like the auth bridge. MFEs consume
 * it through `ShellComposerBridgeProvider` / `useShellComposerBridge`.
 *
 * The transport is intentionally UNTYPED (`emit(channel, detail)` /
 * `subscribe(channel, listener)`): the draft-patch payload types live in this
 * package but the composer *lifecycle* payload types live in
 * `@robinpowered/dashboard-apps-patterns`, and `common` cannot depend on
 * `patterns`. Each channel keeps its own strongly-typed wrapper (see
 * `eventDraftChannel`, `siderChannel`, and patterns' lifecycle channels) that
 * casts at the boundary; only our own typed emitters ever write a channel, so
 * the cast is sound.
 */
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Create a bridge instance. Called once by the shell (in `AppShell`), and once
 * per bundle as a standalone fallback (see `ShellComposerBridgeContext`) so
 * hooks never crash when no shell-provided bridge is present.
 */
export function createShellComposerBridge() {
    var channels = new Map();
    return {
        emit: function (channel, detail) {
            var listeners = channels.get(channel);
            if (!listeners)
                return;
            // Snapshot so a listener that (un)subscribes during dispatch doesn't
            // mutate the set we're iterating.
            __spreadArray([], __read(listeners), false).forEach(function (listener) { return listener(detail); });
        },
        subscribe: function (channel, listener) {
            var listeners = channels.get(channel);
            if (!listeners) {
                listeners = new Set();
                channels.set(channel, listeners);
            }
            listeners.add(listener);
            return function () {
                listeners === null || listeners === void 0 ? void 0 : listeners.delete(listener);
            };
        },
    };
}
